import React, { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import ApexCharts from "apexcharts";
import { API_BASE_URL } from "../../../../../environment";

interface SubscriptionStats {
  total_active_free_subscriptions: number;
  total_inactive_free_subscriptions: number;
  total_active_paid_subscriptions: number;
  total_inactive_paid_subscriptions: number;
  total_active_subscriptions: number;
  total_inactive_subscriptions: number;
}

interface AnalyticsData {
  stats: SubscriptionStats;
}

const PieChart = () => {
  const [data, setData] = useState<AnalyticsData | null>(null);
  const [loading, setLoading] = useState(true);
  const chartRef = useRef<HTMLDivElement | null>(null);
  const chartInstanceRef = useRef<ApexCharts | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        const authHeader = localStorage.getItem("authCred") || "";
        const response = await fetch(
          `${API_BASE_URL}/dashboard/tenant-subscription-analytics/`,
          {
            headers: {
              Authorization: `Basic ${authHeader}`,
            },
          }
        );

        if (!response.ok) {
          throw new Error(`HTTP error! status: ${response.status}`);
        }

        const result = await response.json();
        setData(result);
      } catch (error) {
        console.error("Error fetching subscription analytics:", error);
        setData(null);
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    if (chartInstanceRef.current) {
      chartInstanceRef.current.destroy();
      chartInstanceRef.current = null;
    }

    if (chartRef.current && data && data.stats) {
      const freeSubscriptions = data.stats.total_active_free_subscriptions + data.stats.total_inactive_free_subscriptions;
      const paidSubscriptions = data.stats.total_active_paid_subscriptions + data.stats.total_inactive_paid_subscriptions;
      const totalSubscriptions = freeSubscriptions + paidSubscriptions;

      if (totalSubscriptions === 0) return;

      const freePercentage = Math.round((freeSubscriptions / totalSubscriptions) * 100);
      const paidPercentage = Math.round((paidSubscriptions / totalSubscriptions) * 100);

      const options = {
        series: [freePercentage, paidPercentage],
        chart: {
          type: "donut",
          height: 200
        },
        labels: ["Trial Subscription", "Cloudee Sales"],
        colors: ["#FC2647", "#26A19F"],
        dataLabels: {
          enabled: false
        },
        legend: {
          show: false
        },
        plotOptions: {
          pie: {
            donut: {
              size: "65%"
            }
          }
        },
        tooltip: {
          y: {
            formatter: (val: number, opts: any) => {
              const seriesIndex = opts.seriesIndex;
              if (seriesIndex === 0) {
                return `${freeSubscriptions}`;
              } else {
                return `${paidSubscriptions}`;
              }
            }
          }
        }
      };

      try {
        chartInstanceRef.current = new ApexCharts(chartRef.current, options);
        chartInstanceRef.current.render();
      } catch (error) {
        console.error("Error creating pie chart:", error);
      }
    }

    return () => {
      if (chartInstanceRef.current) {
        try {
          chartInstanceRef.current.destroy();
        } catch (error) {
          console.warn("Error destroying pie chart:", error);
        }
        chartInstanceRef.current = null;
      }
    };
  }, [data]);

  const getFreePercentage = () => {
    if (!data?.stats) return 0;
    const freeSubscriptions = data.stats.total_active_free_subscriptions + data.stats.total_inactive_free_subscriptions;
    const paidSubscriptions = data.stats.total_active_paid_subscriptions + data.stats.total_inactive_paid_subscriptions;
    const totalSubscriptions = freeSubscriptions + paidSubscriptions;
    return totalSubscriptions === 0 ? 0 : Math.round((freeSubscriptions / totalSubscriptions) * 100);
  };

  const getPaidPercentage = () => {
    if (!data?.stats) return 0;
    const freeSubscriptions = data.stats.total_active_free_subscriptions + data.stats.total_inactive_free_subscriptions;
    const paidSubscriptions = data.stats.total_active_paid_subscriptions + data.stats.total_inactive_paid_subscriptions;
    const totalSubscriptions = freeSubscriptions + paidSubscriptions;
    return totalSubscriptions === 0 ? 0 : Math.round((paidSubscriptions / totalSubscriptions) * 100);
  };

  return (    
    <div className="col-md-6 d-flex">
      <div className="card flex-fill">
        <div className="card-header d-flex align-items-center justify-content-between flex-wrap gap-2">
          <h6 className="mb-0 d-flex align-items-center">
            <span className="db-chart-icon">
              <i className="ti ti-chart-pie" />
            </span>
            Subscription Distribution
          </h6>
        </div>
        <div className="card-body">
          {loading ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 250 }}>
              <div className="text-center">
                <div className="spinner-border text-primary mb-2" role="status">
                  <span className="visually-hidden">Loading...</span>
                </div>
                <p className="text-muted">Loading chart data...</p>
              </div>
            </div>
          ) : !data?.stats ? (
            <div className="d-flex justify-content-center align-items-center" style={{ minHeight: 250 }}>
              <div className="text-center">
                <i className="ti ti-chart-pie text-muted mb-3" style={{ fontSize: "3rem" }}></i>
                <h5 className="text-muted">No Data Available</h5>
                <p className="text-muted">No subscription data found</p>
              </div>
            </div>
          ) : (
            <>
              <div id="subscription-distribution" ref={chartRef} />
              <div className="mt-3">
                <div className="d-flex align-items-center justify-content-between mb-3">
                  <p className="f-14 fw-medium text-dark mb-0">
                    <i className="ti ti-circle-filled text-danger me-1" />
                    Trial Subscription
                  </p>
                  <p className="f-14 fw-medium text-dark mb-0">{getFreePercentage()}%</p>
                </div>
                <div className="d-flex align-items-center justify-content-between mb-0">
                  <p className="f-14 fw-medium text-dark mb-0">
                    <i className="ti ti-circle-filled text-info me-1" />
                    Cloudee Sales
                  </p>
                  <p className="f-14 fw-medium text-dark mb-0">{getPaidPercentage()}%</p>
                </div>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};

export default PieChart;